import { memo, useCallback, useMemo, useState } from 'react';

import { type Board } from '@air/api/types';
import { ChevronRightIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { ItemPickerSidebarBoardRow } from '~/components/ItemPicker/ItemPickerSidebarBoardRow';
import { GENERAL_LIBRARY_ID } from '~/components/ItemPicker/itemPickerConstants';
import {
  type ItemPickerBoardSelectParams,
  type ItemPickerLibrarySelectParams,
} from '~/components/ItemPicker/itemPickerTypes';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { usePrivateGeneralLibraryBoardsList } from '~/swr-hooks/boards/usePrivateGeneralLibraryBoardsList';

const GENERAL_LIBRARY_FALLBACK_LABEL = 'General';
const GENERAL_LIBRARY_FALLBACK_ICON = '🏠';

export type ItemPickerSidebarGeneralLibraryRowProps = {
  activeBoardId?: Board['id'];
  isActive: boolean;
  onSelectBoard: (params: ItemPickerBoardSelectParams) => void;
  onSelectLibrary: (params: ItemPickerLibrarySelectParams) => void;
};

export const ItemPickerSidebarGeneralLibraryRow = memo(
  ({ activeBoardId, isActive, onSelectBoard, onSelectLibrary }: ItemPickerSidebarGeneralLibraryRowProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { currentWorkspace } = useCurrentWorkspace();
    const label = currentWorkspace?.defaultLibrary ?? GENERAL_LIBRARY_FALLBACK_LABEL;
    const icon = currentWorkspace?.defaultLibraryIcon ?? GENERAL_LIBRARY_FALLBACK_ICON;

    const { data } = usePrivateGeneralLibraryBoardsList({ workspaceId: currentWorkspace?.id }, { enabled: isExpanded });
    const boards = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);

    const handleSelectLibrary = useCallback(() => {
      onSelectLibrary({ libraryId: GENERAL_LIBRARY_ID, label });
    }, [label, onSelectLibrary]);

    const handleToggleExpanded = useCallback(() => {
      setIsExpanded((prev) => !prev);
    }, []);

    return (
      <div>
        <div
          className={cn(
            'flex h-10 items-center gap-2 rounded-4 px-2 font-medium text-14 transition-colors',
            isActive
              ? 'ads:bg-subtle bg-gray-3 font-semibold ads:text-primary text-gray-12'
              : 'ads:text-secondary text-gray-11 ads:hover:bg-hover-subtle hover:bg-gray-3',
          )}
          data-testid="ITEM_PICKER_SIDEBAR_GENERAL_LIBRARY_ROW"
        >
          <button
            aria-current={isActive ? 'true' : undefined}
            className="flex min-w-0 grow items-center gap-2 rounded-4 text-left outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset"
            onClick={handleSelectLibrary}
            type="button"
          >
            <div className="flex size-6 shrink-0 items-center justify-center rounded-2 border ads:border-default border-gray-4 ads:bg-ads-gray-4 bg-gray-4 ads:text-primary text-10 text-gray-12">
              {icon}
            </div>
            <span className="grow truncate">{label}</span>
          </button>
          <button
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${label}`}
            className="flex size-5 shrink-0 items-center justify-center rounded-2 ads:text-icon-subtle text-gray-9 outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset"
            onClick={handleToggleExpanded}
            type="button"
          >
            <ChevronRightIcon className={cn('transition-transform', isExpanded && 'rotate-90')} size="xs" />
          </button>
        </div>

        {isExpanded &&
          boards.map((board) => (
            <ItemPickerSidebarBoardRow
              activeBoardId={activeBoardId}
              board={board}
              depth={0}
              key={board.id}
              onSelectBoard={onSelectBoard}
            />
          ))}
      </div>
    );
  },
);

ItemPickerSidebarGeneralLibraryRow.displayName = 'ItemPickerSidebarGeneralLibraryRow';
